import { shallowRef, readonly, type Ref } from 'vue'

export type SnackbarVariant = 'approve' | 'sendBack' | 'saved' | 'restore' | 'info' | 'blocked'

export type SnackbarMessage = {
  text: string
  variant?: SnackbarVariant
  action?: { label: string; handler?: () => void; kind?: 'undo' }
  timeout?: number
  testId?: string
}

export type QueuedSnackbar = SnackbarMessage & { id: number; timeout: number }

let nextId = 0
const current = shallowRef<QueuedSnackbar | null>(null)
const pending = shallowRef<readonly QueuedSnackbar[]>([])

export function useSnackbarQueue(): {
  current: Readonly<Ref<QueuedSnackbar | null>>
  pending: Readonly<Ref<readonly QueuedSnackbar[]>>
  enqueue(m: SnackbarMessage): number
  dismiss(): void
  clear(): void
} {
  function enqueue(m: SnackbarMessage): number {
    const id = ++nextId
    const timeout = m.timeout ?? (m.action?.kind === 'undo' ? 8000 : 5000)
    const item: QueuedSnackbar = { ...m, id, timeout }
    if (!current.value) {
      current.value = item
    } else {
      pending.value = [...pending.value, item]
    }
    return id
  }

  function dismiss(): void {
    if (pending.value.length > 0) {
      const [next, ...rest] = pending.value
      current.value = next
      pending.value = rest
    } else {
      current.value = null
    }
  }

  function clear(): void {
    current.value = null
    pending.value = []
  }

  return {
    current: readonly(current) as Readonly<Ref<QueuedSnackbar | null>>,
    pending: readonly(pending) as Readonly<Ref<readonly QueuedSnackbar[]>>,
    enqueue,
    dismiss,
    clear,
  }
}
